/* eslint-disable no-param-reassign */
import { createSlice } from "@reduxjs/toolkit"
import { defineMessages } from "react-intl"
import _ from "lodash"
import { compareRows } from "../pages/forms/formUtils"
import httpClient from "./httpClient"
import { asBoolean, isString } from "../commons/utils"
import { actions as notifier } from "../component/notifier"

const messages = defineMessages({
  noMoreFormPublished: {
    id: "form.admin.publish.nomore.publication",
    defaultMessage: "All users have already been notified for selected form(s)."
  },
  notifiedUsers: {
    id: "form.admin.publish.notified.users",
    defaultMessage: "{nbUser} notified users."
  }
})

const DEFAULT_FORM = {
  name: "",
  formIdentifier: "form-1",
  authorId: "",
  signable: false,
  strict: false,
  type: "simple",
  frequency: null,
  elements: [],
  rows: [],
  pages: []
}

const initialState = {
  isLoading: false,
  isDirty: false,
  attributes: { values: [], mapping: {} },
  tags: [],
  form: DEFAULT_FORM,
  formElements: {}, // Map in order to help some components (and prevent useless iterations)
  selectedElement: null,
  selectedElementId: null,
  selectedPageIndex: null,

  /**
   * Tracks the maximum ID for a given element type;
   * Key (String) - element type
   * Value (Number) - maximum ID
   */
  elementNextId: {}
}

/**
 * In a list of elements with IDs (e.g., [text-1, text-2, text-4]),
 * return the last maximum ID (e,g., 4)
 *
 * NOTE: Not to be used with a list of elements with mixed types (e.g., [text-1, phone-2])
 * @param elements
 * @returns {number}
 */
function getMaxIdForElementsOfSameType(elements) {
  const elementIds = elements
    .map((element) => element.id)
    .map((id) => id.split("-")[1])
    .map((count) => parseInt(count, 10))

  return Math.max(...elementIds)
}

/**
 * Function used to hold all elements in a temporary map, it will be used to prevent useless iterations in some components
 * */
export function reduceElementsToMap(elements) {
  return elements.reduce((acc, elem) => {
    acc[elem?.id] = elem
    return acc
  }, {})
}

// "elementsIds" is an array of strings of all the ids of the elements to be deleted
function filterRows(rows, elementsIds) {
  return rows.reduce((acc, row) => {
    if (!Array.isArray(row) && !isString(row)) {
      acc.push(row)
      return acc
    }
    if (isString(row)) {
      const elemId = row
      if (elementsIds.includes(elemId)) {
        return acc
      }
      acc.push(elemId)
    }
    acc.push(row.filter((elemId) => !elementsIds.includes(elemId)))
    return acc
  }, [])
}

// "elementsIds" is an array of strings of all the ids of the elements to be deleted
function filterPages(pages, elementsIds) {
  return pages.map((page) => {
    const { elementsIds: elemsIdsOfPage } = page
    return {
      ...page,
      elementsIds: elemsIdsOfPage.filter(
        (elemId) => !elementsIds.includes(elemId)
      )
    }
  })
}

/*
 * This redux slice will be used to store Attributes and Tags available on a form
 * It also contains the current edited form and selected element by the admin, every element will subscribe to their corresponding object
 * Note: All 'no-param-reassign' errors from ESLint here are false,
 * thanks to ImmerJS which allows us to write "mutating" immutable updates (see @https://redux-toolkit.js.org/api/createSlice)
 * */
const formSlice = createSlice({
  initialState,
  name: "form",
  reducers: {
    setPagesForm: (state, action) => {
      state.form.pages = action.payload
    },
    setRowsForm: (state, action) => {
      state.form.rows = action.payload
    },
    setIsLoading: (state, { payload }) => {
      state.isLoading = asBoolean(payload)
    },
    setAttributes: (state, action) => {
      state.attributes = action.payload
    },
    addAttributes: (state, action) => {
      const { values: newValues, mapping: newMapping } = action.payload
      state.attributes.values = state.attributes.values.concat(newValues)
      state.attributes.mapping = {
        ...state.attributes.mapping,
        ...newMapping
      }
    },
    updateAttribute: (state, action) => {
      const { oldAttribute, newAttribute } = action.payload
      const index = state.attributes.values.findIndex(
        (attr) => attr.jumpIdentifier === oldAttribute.jumpIdentifier
      )
      if (index >= 0) {
        state.attributes.values[index] = newAttribute
      }
    },
    removeAttributes: (state, { payload: attrIdsToRemoved }) => {
      state.attributes.values = _.remove(
        state.attributes.values,
        (it) => it._id !== attrIdsToRemoved[0]
      )
    },
    setTags: (state, action) => {
      state.tags = action.payload
    },
    setForm: (state, action) => {
      const form = action.payload
      const elementNextId = {}
      const formElements = form.elements || []
      const elementsByType = _.groupBy(formElements, (element) => element.type)

      Object.entries(elementsByType).forEach(([type, elements]) => {
        elementNextId[type] = getMaxIdForElementsOfSameType(elements)
      })

      form.rows = form.rows || formElements.map(({ id }) => [id])
      state.formElements = reduceElementsToMap(formElements)
      state.elementNextId = elementNextId
      state.form = form
      state.isDirty = false
    },
    incrementIdForElementType(state, action) {
      const { elementType } = action.payload
      if (!state.elementNextId[elementType]) {
        state.elementNextId[elementType] = 1
      } else {
        state.elementNextId[elementType] += 1
      }
    },
    setFormProperty: (state, action) => {
      const { property, value } = action.payload
      state.form[property] = value
      state.isDirty = true
    },
    updateFormElement: (state, action) => {
      const element = action.payload
      if (state.form) {
        state.form.elements = state.form.elements.map((elem) =>
          elem.id === element.id ? element : elem
        )
        state.formElements[element.id] = element
      }
      state.isDirty = true
    },
    updateFormElementProperty: (state, action) => {
      const { index, property, value } = action.payload
      if (state.form) {
        state.form.elements[index][property] = value
        state.formElements = reduceElementsToMap(state.form.elements)
      }
      state.isDirty = true
    },
    updateFormElementDataProperty: (state, action) => {
      const { index, property, value, key } = action.payload
      if (state.form) {
        state.form.elements[index].data[key][property] = value
        state.formElements = reduceElementsToMap(state.form.elements)
      }
      state.isDirty = true
    },
    setSelectedElement: (state, action) => {
      state.selectedElement = action.payload
    },
    setSelectedElementId: (state, action) => {
      state.selectedElementId = action.payload
    },
    setSelectedPageIndex: (state, action) => {
      state.selectedPageIndex = action.payload
    },
    moveElementToPage: (state, action) => {
      if (!state.form.pages) {
        // Should not happen, how did you get here ?
        return
      }
      const { id, newPageIndex } = action.payload
      const currentPage = state.form.pages[state.selectedPageIndex]
      const isPresent = currentPage.elementsIds.find((elemId) => elemId === id)
      if (!isPresent) {
        return
      }
      currentPage.elementsIds = currentPage.elementsIds.filter(
        (elemId) => elemId !== id
      )
      state.form.pages[newPageIndex].elementsIds.push(id)
      // If the form element is a part of an existing row, we must isolate it and create a new row (otherwise it's undefined behaviour)
      const elementRowIndex = state.form.rows?.findIndex(
        (row) => Array.isArray(row) && row.includes(id)
      )
      if (elementRowIndex >= 0 && state.form.rows[elementRowIndex].length > 1) {
        state.form.rows[elementRowIndex] = state.form.rows[
          elementRowIndex
        ].filter((elemId) => elemId !== id)
        state.form.rows.push([id])
      }
      state.selectedPageIndex = newPageIndex
      state.isDirty = true
    },
    addPageToForm: (state, action) => {
      state.form.pages = state.form.pages || [] // Ensure 'pages' exist from now on
      const { title, locale } = action.payload
      const elementsIds =
        state.form.pages.length === 0 // If this is the first page created, move all elements inside this first page
          ? state.form.elements.map((e) => e.id)
          : []
      state.form.pages.push({ title: { [locale]: title }, elementsIds })
      state.selectedPageIndex = state.form.pages.length - 1
      state.isDirty = true
    },
    deletePageFromForm: (state, action) => {
      const deleteAll = action.payload
      // If deleteAll is false, transfer all current element to previous page
      // If deleteAll is true, delete all elements of the page
      const index = state.selectedPageIndex
      state.form.pages = state.form.pages || [] // Ensure 'pages' exist from now on
      if (index === null || state.form.pages.length <= index) {
        return
      }
      const elementsIds = state.form.pages[index].elementsIds || []
      state.form.pages.splice(index, 1)
      state.selectedPageIndex = Math.min(index, state.form.pages.length - 1)
      if (state.selectedPageIndex < 0) {
        state.selectedPageIndex = null
      }

      state.isDirty = true
      if (
        elementsIds.length <= 0 ||
        (deleteAll !== true && deleteAll !== false)
      ) {
        return
      }

      if (!deleteAll && state.selectedPageIndex !== null) {
        const previousPage = state.form.pages[state.selectedPageIndex]
        previousPage.elementsIds = previousPage.elementsIds.concat(elementsIds)
      }
      if (deleteAll) {
        // state.form.elements = state.form.elements.filter((elem) =>
        //   elementsIds.includes(elem.id)
        // )
        // console.log(state.selectedElementId)
        // console.log(state.selectedElementId)
        //
        // if (elementsIds.includes(state.selectedElementId)) {
        //   state.selectedElementId = null
        //   state.selectedElement = null
        // }
        // state.formElements = reduceElementsToMap(state.form.elements)
      }
    },
    renamePageOfForm: (state, action) => {
      state.form.pages = state.form.pages || [] // Ensure 'pages' exist from now on
      const { index, locale, title } = action.payload
      if (state.form.pages.length <= index) {
        return
      }
      state.form.pages[index].title[locale] = title
      state.isDirty = true
    },
    addElementToForm: (state, action) => {
      const element = action.payload
      const formElements = state.form.elements || []

      state.form.elements = [...formElements, element]
      state.formElements[element.id] = element

      if (
        state.selectedPageIndex !== null &&
        state.form.pages.length > state.selectedPageIndex
      ) {
        const elementsIds =
          state.form.pages[state.selectedPageIndex].elementsIds || []
        elementsIds.push(element.id)
        state.form.pages[state.selectedPageIndex].elementsIds = elementsIds
      }
    },
    // For this action, row must be correctly setup, with attributes and type set.
    addRowToForm: (state, action) => {
      const { row, index } = action.payload
      const newRows = state.form.rows || []
      if (index === undefined) {
        newRows.push(row)
      }

      state.form.rows = newRows
      if (index !== undefined) {
        const left = newRows.slice(0, index)
        const right = newRows.slice(index, state.form.rows.length)
        state.form.rows = [...left, row, ...right]
      }
      state.isDirty = true
    },
    addElementToRow: (state, action) => {
      const { element, rowIndex } = action.payload
      const { id } = element
      const targetRow = state.form.rows[rowIndex] || []
      state.form.rows[rowIndex] = [...targetRow, id]
      state.isDirty = true
    },
    moveElementUp: (state, action) => {
      const index = action.payload
      const prevElem = state.form.elements[index - 1]
      const newElements = state.form.elements.slice()
      newElements[index - 1] = newElements[index]
      newElements[index] = prevElem
      state.form.elements = newElements
      state.isDirty = true
    },
    moveElementDown: (state, action) => {
      const index = action.payload
      const nextElem = state.form.elements[index + 1]
      const newElements = state.form.elements.slice()
      newElements[index + 1] = newElements[index]
      newElements[index] = nextElem
      state.form.elements = newElements
      state.isDirty = true
    },
    moveRow: (state, action) => {
      const { srcIndex, desIndex } = action.payload
      const previousRow = state.form.rows[desIndex]
      const newRows = state.form.rows.slice()
      newRows[desIndex] = newRows[srcIndex]
      newRows[srcIndex] = previousRow
      state.form.rows = newRows
      state.isDirty = true
    },
    handleDragAndDrop: (state, action) => {
      const { srcRow, desRow } = action.payload
      const formRows =
        state.form.rows || state.form.elements.map(({ id }) => [id])
      // Here, offset is used to take into account the previous pages
      const srcRowIndex = formRows.findIndex((row) => compareRows(row, srcRow))
      const desRowIndex = formRows.findIndex((row) => compareRows(row, desRow))
      const tmp = state.form.rows
        ? state.form.rows[srcRowIndex]
        : state.form.elements[srcRowIndex]
      // If rows don't exist in the current form, replace elements directly
      if (!state.form.rows) {
        state.form.elements[srcRowIndex] = state.form.elements[desRowIndex]
        state.form.elements[desRowIndex] = tmp
      }
      if (state.form.rows) {
        state.form.rows[srcRowIndex] = state.form.rows[desRowIndex]
        state.form.rows[desRowIndex] = tmp
      }
      state.isDirty = true
    },
    deleteElement: (state, action) => {
      const { elementId } = action.payload

      state.form.elements = state.form.elements.filter(
        ({ id }) => id !== elementId
      )
      state.formElements = reduceElementsToMap(state.form.elements)
      state.form.rows = filterRows(state.form.rows, [elementId]) // Update rows
      state.form.pages = filterPages(state.form.pages, [elementId]) // Update pages
      if (state.selectedElementId === elementId) {
        state.selectedElementId = null
        state.selectedElement = null
      }
      state.isDirty = true
    },
    deleteElements: (state, action) => {
      const { elementsIds } = action.payload
      // "elementsIds" is an array of strings of all the ids of the elements to be deleted

      state.form.elements = state.form.elements.filter(
        ({ id }) => !elementsIds.includes(id)
      )
      state.formElements = reduceElementsToMap(state.form.elements)
      state.form.rows = filterRows(state.form.rows, elementsIds) // Update rows
      state.form.pages = filterPages(state.form.pages, elementsIds) // Update pages
      if (elementsIds.includes(state.selectedElementId)) {
        state.selectedElementId = null
        state.selectedElement = null
      }
      state.isDirty = true
    },
    deleteRow: (state, action) => {
      // FIXME MLa : Est-ce que les éléments sont vraiment supprimés du formulaire (si oui, faire une popup pour l'indiquer) ? Est-ce qu'il faut laisser le choix à l'utilisateur ?
      const index = action.payload
      const newArray = state.form.rows.slice()
      const elementsIds = state.form.rows[index]
      if (elementsIds.includes(state.selectedElementId)) {
        state.selectedElementId = null
        state.selectedElement = null
      }
      newArray.splice(index, 1)
      state.form.rows = newArray
      const newElements = state.form.elements.filter(
        ({ id }) => !elementsIds.includes(id)
      )
      state.form.elements = newElements
      state.formElements = reduceElementsToMap(newElements)
      state.isDirty = true
    }
  }
})

function fetchContactBasket(cancelToken) {
  return () => httpClient.get("/api/contact/basket", { cancelToken })
}

function resolveBasket(formValues) {
  const { basket, publicationMode } = formValues
  return () =>
    httpClient.get(
      `/api/contact/basket/${basket}/resolve?mode=${publicationMode}`
    )
}

function publish(formValues) {
  return (dispatch) => {
    return httpClient.post("/api/form/publish", formValues).then((response) => {
      const nbUser = response.data
      const action =
        nbUser === 0
          ? notifier.enqueueInfo(messages.noMoreFormPublished)
          : notifier.enqueueSuccess(messages.notifiedUsers, {
              intlOptions: { nbUser }
            })

      dispatch(action)
    })
  }
}

function relaunch(userformId) {
  return (dispatch) => {
    return httpClient.post("/api/form/relaunch", { userformId }).then(() => {
      dispatch(
        notifier.enqueueSuccess(messages.notifiedUsers, {
          intlOptions: { nbUser: 1 }
        })
      )
    })
  }
}

function createForm(formToSave) {
  return (dispatch) => {
    dispatch(formSlice.actions.setIsLoading(true))
    return httpClient
      .post("/api/forms", formToSave)
      .finally(() => dispatch(formSlice.actions.setIsLoading(false)))
  }
}

function saveForm() {
  return async (ignored, getState) => {
    const {
      formSlice: { form }
    } = getState()
    const formToSave = {
      _id: form._id,
      name: form.name,
      formIdentifier: form.formIdentifier,
      authorId: form.authorId,
      signable: form.signable,
      strict: form.strict,
      type: form.type,
      frequency: form.frequency,
      elements: form.elements,
      rows: form.rows,
      pages: form.pages
    }
    await httpClient.put("/api/forms", formToSave)
  }
}

function fetchContactAttributes(cancelToken) {
  return (dispatch) => {
    dispatch(formSlice.actions.setIsLoading(true))
    return httpClient
      .get("/api/contact/attributes", { cancelToken })
      .then(({ data }) => {
        dispatch(
          formSlice.actions.setAttributes({
            values: data.values,
            mapping: data.mapping
          })
        )
      })
      .finally(() => dispatch(formSlice.actions.setIsLoading(false)))
  }
}

// Get all attributes for a specific element
function fetchElementContactAttributes(element) {
  return (dispatch, getState) => {
    const {
      formSlice: { form }
    } = getState()
    dispatch(formSlice.actions.setIsLoading(true))
    return httpClient
      .post("/api/contact/attributes/element", { form, element })
      .then(({ data }) => {
        dispatch(
          formSlice.actions.addAttributes({
            values: data.values,
            mapping: data.mapping
          })
        )
        return data.values
      })
      .finally(() => dispatch(formSlice.actions.setIsLoading(false)))
  }
}

/**
 * Creates a new element object with its own unique ID
 * @param element
 */
function makeNewElement(element) {
  return async (dispatch, getState) => {
    const elementType = element.type

    // Increment before any network requests (e.g., fetchElementContactAttributes)
    // to avoid nasty bugs (e.g., same ID bug)
    await dispatch(formSlice.actions.incrementIdForElementType({ elementType }))

    // Retrieve elementNextId after incrementing to get the latest data
    const { elementNextId } = getState().formSlice

    const newElement = {
      ...element.default,
      type: elementType,
      id: elementType.concat("-", elementNextId[elementType])
    }

    // Each attribute is stored as an object; It will be converted to IDs when the admin saves
    newElement.jumpAttributeIds = await dispatch(
      fetchElementContactAttributes(newElement)
    )

    return newElement
  }
}

/**
 * Adds a row that contains only one element
 * @param element
 * @param index
 * @returns {function(*, *): *}
 */
function addUniqueElementRowToForm(element) {
  return async (dispatch) => {
    const newElement = await dispatch(makeNewElement(element))
    const newRow = [newElement.id]
    await dispatch(formSlice.actions.addElementToForm(newElement))
    return dispatch(formSlice.actions.addRowToForm({ row: newRow }))
  }
}

function addElementToRow(element, rowIndex) {
  return async (dispatch) => {
    const newElement = await dispatch(makeNewElement(element))
    await dispatch(formSlice.actions.addElementToForm(newElement))
    return dispatch(
      formSlice.actions.addElementToRow({ element: newElement, rowIndex })
    )
  }
}

// FIXME
function addElementToForm(elem, index) {
  return async (dispatch, getState) => {
    const elementType = elem.type

    // Increment before any network requests (e.g., fetchElementContactAttributes)
    // to avoid nasty bugs (e.g., same ID bug)
    await dispatch(formSlice.actions.incrementIdForElementType({ elementType }))

    // Retrieve elementNextId after incrementing to get the latest data
    const { elementNextId } = getState().formSlice

    const id = elementType.concat("-", elementNextId[elementType])
    const newElem = {
      ...elem.default,
      type: elementType,
      id
    }

    // Currently, every attributes are stored as objects, they will be converted as Ids when admin saves
    newElem.jumpAttributeIds = await dispatch(
      fetchElementContactAttributes(newElem)
    )

    return dispatch(
      formSlice.actions.addElementToForm({ element: newElem, index })
    )
  }
}

function duplicateElement(index) {
  return (dispatch, getState) => {
    const {
      formSlice: { form }
    } = getState()
    const newElem = {
      type: form.elements[index].type,
      default: { ...form.elements[index] } // 'default' will be used in addElementToForm(), we use it to transfer parameters
    }
    return dispatch(addElementToForm(newElem, index))
  }
}

function duplicateRow(index) {
  function duplicateRowElement(element) {
    return {
      type: element.type,
      default: { ...element } // 'default' will be used in addRowToForm(), we use it to transfer parameters
    }
  }

  return (dispatch, getState) => {
    const {
      formSlice: { form }
    } = getState()

    const targetRow = form.rows[index]
    const newRow = targetRow.map(duplicateRowElement)

    return dispatch(formSlice.actions.addRowToForm({ row: newRow, index }))
  }
}

function updateFormElement(element) {
  return (dispatch) => dispatch(formSlice.actions.updateFormElement(element))
}

function updateFormElementProperty(element, property, value) {
  if (property === "id") {
    // eslint-disable-next-line no-console
    console.warn(
      `updateFormElementProperty() is called with 'id' property (current value : ${element.id} / new value : ${value})`
    )
  }
  const newElement = _.cloneDeep(element)
  _.set(newElement, property, value)
  return updateFormElement(newElement)
}

function updateFormElementDataProperty(element, property, value, key) {
  const newElement = _.cloneDeep(element)
  _.set(newElement, ["data", key, property], value)
  return updateFormElement(newElement)
}

function updateFormElementsAttribute(oldAttribute, newAttribute) {
  return (dispatch, getState) => {
    if (!oldAttribute || !newAttribute) {
      return Promise.reject()
    }
    const {
      formSlice: { form }
    } = getState()
    const newElements = form.elements.map((e) => {
      return {
        ...e,
        jumpAttributeIds: e.jumpAttributeIds.map((attr) =>
          attr.jumpIdentifier === oldAttribute.jumpIdentifier
            ? newAttribute
            : attr
        )
      }
    })
    const newForm = { ...form, elements: newElements }
    return dispatch(formSlice.actions.setForm(newForm))
  }
}

function updateContactAttribute(oldAttribute, newAttribute) {
  return (dispatch) => {
    if (!newAttribute._id) {
      return dispatch(
        formSlice.actions.updateAttribute({ oldAttribute, newAttribute })
      )
    }
    return httpClient
      .put("/api/contact/attributes", newAttribute)
      .then(() =>
        dispatch(
          formSlice.actions.updateAttribute({ oldAttribute, newAttribute })
        )
      )
  }
}

function deleteContactAttributes(attributeIds) {
  return (dispatch) => {
    dispatch(formSlice.actions.setIsLoading(true))
    const promise =
      attributeIds.length === 1
        ? httpClient.delete(`/api/contact/attributes/${attributeIds[0]}`)
        : httpClient.patch("/api/contact/attributes", attributeIds)
    return promise
      .then((deletedAttributeIds) => {
        dispatch(formSlice.actions.removeAttributes(deletedAttributeIds))
      })
      .finally(() => dispatch(formSlice.actions.setIsLoading(false)))
  }
}

function fetchUserTags(cancelToken) {
  return (dispatch) => {
    dispatch(formSlice.actions.setIsLoading(true))
    return httpClient
      .get("/api/tags", { cancelToken }) // TODO: /api/tags -> /api/user/tags
      .then(({ data: tags }) => dispatch(formSlice.actions.setTags(tags)))
      .finally(() => dispatch(formSlice.actions.setIsLoading(false)))
  }
}

function generateNewFormElementAttributes(element) {
  return (dispatch, getState) => {
    const {
      formSlice: { form }
    } = getState()
    return httpClient
      .post("/api/contact/attributes/element", { form, element })
      .then(({ data }) => {
        dispatch(formSlice.actions.addAttributes(data))
        return data
      })
  }
}

export default {
  initialState,
  actions: {
    ...formSlice.actions,
    fetchContactAttributes,
    fetchElementContactAttributes,
    addUniqueElementRowToForm,
    addElementToRow,
    duplicateRow,
    addElementToForm,
    duplicateElement,
    updateFormElement,
    updateFormElementProperty,
    updateFormElementDataProperty,
    updateFormElementsAttribute,
    fetchContactBasket,
    resolveBasket,
    publish,
    relaunch,
    createForm,
    saveForm,
    updateContactAttribute,
    deleteContactAttributes,
    generateNewFormElementAttributes,
    fetchUserTags
  },
  reducer: formSlice.reducer
}
